// components/.../MemoryCreation.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from '../../public/icons';
import BrowserPreview from './BrowserPreview';
import AudioRecorder from '../general/AudioRecorder';
import {
  CautionIcon,
  UploadCloudIcon,
  MusicFileIcon,
  RecordOutlineIcon
} from '../../public/icons';
import reviewService from '../../services/reviewService';
import orderService from '../../services/ordersRouteServce';

const MemoryCreation = () => {
  const [step, setStep] = useState(1);
  const [subStep, setSubStep] = useState(1);

  /* ── Read incoming nav state from OrderModal ── */
  const location = useLocation();
  const {
    orderId,
    giftId,                 // 🟢 stable id (primary key)
    giftIndex,              // kept for fallback / debugging
    gift: incomingGift
  } = location.state || {};

  /* ── Live preview fields ── */
  const [pageSlug, setPageSlug] = useState('');
  const [memoryTitle, setMemoryTitle] = useState('');
  const [description, setDescription] = useState('');

  /* ── Reveal ── */
  const [revealDate, setRevealDate] = useState('2025-12-19');
  const [revealTime, setRevealTime] = useState('15:14');

  /* ── Media ── */
  const [uploadedImages, setUploadedImages] = useState([]); // { url, publicId, name, type }
  const [isUploadingMedia, setIsUploadingMedia] = useState(false);
  const [mediaError, setMediaError] = useState('');

  /* ── Audio ── */
  const [audioFile, setAudioFile] = useState(null); // { url, publicId, name, duration }
  const [audioUrlInput, setAudioUrlInput] = useState('');
  const [showAudioRecorder, setShowAudioRecorder] = useState(false);
  const [isUploadingAudio, setIsUploadingAudio] = useState(false);
  const [audioError, setAudioError] = useState('');

  /* ── Saving ── */
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  /* ── Media types checkboxes ── */
  const [mediaTypes, setMediaTypes] = useState({
    photos: true,
    videos: true,
    audio: true,
    writtenNote: true
  });

  const [currentMonth, setCurrentMonth] = useState(11);
  const [currentYear, setCurrentYear] = useState(2025);
  const [isExpanded, setIsExpanded] = useState(false);
  const [textareaRef] = useState(React.createRef());
  const navigate = useNavigate();

  /* ── Pre-fill from incoming gift (title, date) ── */
  useEffect(() => {
    if (incomingGift?.title) setMemoryTitle(incomingGift.title);
    if (incomingGift?.deliveryDate) {
      const d = new Date(incomingGift.deliveryDate);
      if (!isNaN(d.getTime())) {
        setRevealDate(d.toISOString().split('T')[0]);
        setCurrentMonth(d.getMonth());
        setCurrentYear(d.getFullYear());
      }
    }
  }, [incomingGift]);

  /* ── Load any previously saved content for this slot ──
     Keyed by giftId (stable). Falls back to giftIndex only if
     giftId is missing (legacy orders that predate the backfill). */
  useEffect(() => {
    if (!orderId) return;
    const key = giftId ?? giftIndex;
    if (key === undefined || key === null) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await orderService.getFutureGiftContent(orderId, key);
        if (cancelled) return;
        const data = res?.data;
        if (!data) return;

        if (data.pageSlug) setPageSlug(data.pageSlug);
        if (data.memoryTitle) setMemoryTitle(data.memoryTitle);
        if (data.description) setDescription(data.description);
        if (Array.isArray(data.media)) setUploadedImages(data.media);
        if (data.audioFile?.url) setAudioFile(data.audioFile);
        if (data.audioUrl) setAudioUrlInput(data.audioUrl);
        if (data.revealDate) {
          const d = new Date(data.revealDate);
          if (!isNaN(d.getTime())) {
            setRevealDate(d.toISOString().split('T')[0]);
            setCurrentMonth(d.getMonth());
            setCurrentYear(d.getFullYear());
          }
        }
        if (data.revealTime) setRevealTime(data.revealTime);
      } catch (err) {
        console.error('Failed to load saved content:', err);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orderId, giftId, giftIndex]);

  /* ── The audio URL we actually feed the preview with ── */
  const previewAudioUrl = audioFile?.url || audioUrlInput || '';

  /* ── Is there anything worth previewing yet? ── */
  const hasPreviewContent = !!(
    pageSlug.trim() ||
    memoryTitle.trim() ||
    description.trim() ||
    previewAudioUrl ||
    uploadedImages.length > 0
  );

  /* ─────────── Navigation ─────────── */
  const handleBack = () => {
    if (step === 2 && subStep > 1) setSubStep(subStep - 1);
    else if (step > 1) {
      setStep(step - 1);
      setSubStep(1);
    } else navigate('/account/future-gifts');
  };

  const handleNext = async () => {
    /* Move between sub-steps / main steps */
    if (step === 2 && subStep < 4) {
      setSubStep(subStep + 1);
      return;
    }
    if (step < 3) {
      setStep(step + 1);
      setSubStep(1);
      return;
    }

    /* ── Final step: persist the memory ── */
    setSaveError('');

    const key = giftId ?? giftIndex;
    if (!orderId || key === undefined || key === null) {
      console.warn('No orderId/giftId in nav state — nothing to save to.');
      navigate('/account/future-gifts');
      return;
    }

    setIsSaving(true);
    try {
      await orderService.saveFutureGiftContent(orderId, key, {
        pageSlug,
        memoryTitle,
        description,
        media: uploadedImages,
        audioFile: audioFile,
        audioUrl: audioUrlInput,
        revealDate,
        revealTime
      });

      navigate('/account/future-gifts', {
        state: { justSaved: true, orderId, giftId: key }
      });
    } catch (err) {
      console.error('Failed to save memory:', err);
      setSaveError(
        err?.response?.data?.message ||
          err?.message ||
          'Could not save your memory. Please try again.'
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleSkip = () => {
    if (step === 2 && subStep === 3) setSubStep(4);
    else if (step === 2 && subStep < 4) setSubStep(subStep + 1);
    else handleNext();
  };

  /* ─────────── Media type checkboxes ─────────── */
  const handleCheckboxChange = (type) => {
    setMediaTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  /* ─────────── Text formatting ─────────── */
  const formatText = (format) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = description.substring(start, end);

    let formatted = selected;
    if (format === 'bold') formatted = `**${selected}**`;
    if (format === 'italic') formatted = `*${selected}*`;

    setDescription(
      description.substring(0, start) + formatted + description.substring(end)
    );
  };

  const insertEmoji = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    setDescription(
      description.substring(0, start) + emoji + description.substring(start)
    );
  };

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  /* ─────────── Calendar ─────────── */
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const getDaysInMonth = (m, y) => new Date(y, m + 1, 0).getDate();
  const getFirstDayOfMonth = (m, y) => new Date(y, m, 1).getDay();

  const handleDateSelect = (day) => {
    const d = new Date(currentYear, currentMonth, day);
    setRevealDate(d.toISOString().split('T')[0]);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const daysInPrevMonth = getDaysInMonth(currentMonth - 1, currentYear);
    const calendarDays = [];

    for (let i = firstDay - 1; i >= 0; i--)
      calendarDays.push({ day: daysInPrevMonth - i, isCurrentMonth: false });
    for (let day = 1; day <= daysInMonth; day++)
      calendarDays.push({ day, isCurrentMonth: true });
    const remaining = 42 - calendarDays.length;
    for (let day = 1; day <= remaining; day++)
      calendarDays.push({ day, isCurrentMonth: false });

    const selected = new Date(revealDate);
    const sd = selected.getDate();
    const sm = selected.getMonth();
    const sy = selected.getFullYear();

    return (
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((d, i) => {
          const isSelected =
            d.isCurrentMonth && d.day === sd && currentMonth === sm && currentYear === sy;
          return (
            <button
              key={i}
              onClick={() => d.isCurrentMonth && handleDateSelect(d.day)}
              className={`w-12 h-12 flex items-center justify-center text-sm font-medium
                ${d.isCurrentMonth
                  ? isSelected
                    ? 'bg-[#55000C] text-white'
                    : 'text-[#1C2A46] hover:bg-gray-100'
                  : 'text-gray-400'}
                ${d.isCurrentMonth ? 'cursor-pointer' : 'cursor-default'}`}
              disabled={!d.isCurrentMonth}
            >
              {d.day}
            </button>
          );
        })}
      </div>
    );
  };

  const stepTitles = ['Memory Details', 'Memory Details', 'Reveal & Timing'];

  /* ─────────── Media upload (unlimited, via Cloudinary) ─────────── */
  const handleMediaUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    setMediaError('');
    setIsUploadingMedia(true);

    const uploaded = [];
    for (const file of files) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      if (!isImage && !isVideo) continue;
      if (file.size > 50 * 1024 * 1024) {
        setMediaError(`${file.name} exceeds 50MB and was skipped.`);
        continue;
      }

      try {
        const result = await reviewService.uploadImageToCloudinary(file);
        uploaded.push({
          url: result.url,
          publicId: result.publicId,
          name: file.name,
          type: isVideo ? 'video' : 'image'
        });
      } catch (err) {
        console.error('Upload failed:', file.name, err);
        setMediaError(`Failed to upload ${file.name}.`);
      }
    }

    setUploadedImages((prev) => [...prev, ...uploaded]);
    setIsUploadingMedia(false);
    event.target.value = '';
  };

  const removeMedia = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  /* ─────────── Audio upload ─────────── */
  const handleAudioUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setAudioError('');
    if (!file.type.startsWith('audio/')) {
      setAudioError('Please select an audio file.');
      return;
    }
    if (file.size > 14 * 1024 * 1024) {
      setAudioError('Audio must be under 14MB.');
      return;
    }

    setIsUploadingAudio(true);
    try {
      const result = await reviewService.uploadAudioToCloudinary(file);
      setAudioFile({
        url: result.url,
        publicId: result.publicId,
        name: file.name,
        duration: result.duration || null
      });
      setAudioUrlInput('');
    } catch (err) {
      console.error('Audio upload failed:', err);
      setAudioError('Failed to upload audio.');
    } finally {
      setIsUploadingAudio(false);
      event.target.value = '';
    }
  };

  const handleRemoveAudio = () => {
    setAudioFile(null);
    setAudioError('');
  };

  /* ─────────── Render step content ─────────── */
  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-8 text-left">
            <div>
              <h1 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Add Your Digital Memory
              </h1>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                Upload photos, audio, or video, or write a heartfelt message.
                Your loved one will unlock this on the special date you've
                chosen.
              </p>

              <div className="space-y-4">
                {[
                  { key: 'photos', label: 'Photos' },
                  { key: 'videos', label: 'Videos' },
                  { key: 'audio', label: 'Audio' },
                  { key: 'writtenNote', label: 'Written Note' }
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-start space-x-4">
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={mediaTypes[key]}
                        onChange={() => handleCheckboxChange(key)}
                      />
                      <div
                        className={`w-6 h-6 border-2 rounded-[4px] flex items-center justify-center transition-colors ${
                          mediaTypes[key]
                            ? 'bg-[#55000C] border-[#55000C]'
                            : 'border-[#6B635D]'
                        }`}
                      >
                        <svg
                          className="w-4 h-4 text-[#F7F5EF]"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="3"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      </div>
                    </label>
                    <h3 className="font-normal text-secondary text-base mb-1">
                      {label}
                    </h3>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      case 2:
        return renderSubStepContent();

      case 3:
        return (
          <div className="space-y-2 text-left">
            <div>
              <h1 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Reveal & Timing
              </h1>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                Decide if this memory should be available immediately or
                revealed at a specific time.
              </p>
            </div>

            <div className="bg-[#F7F5EF] border border-[#E4E4E4] p-6">
              <div className="flex items-center justify-between mb-6">
                <select
                  value={currentMonth}
                  onChange={(e) => setCurrentMonth(parseInt(e.target.value))}
                  className="text-lg font-medium text-[#1C2A46] bg-transparent border-none outline-none cursor-pointer"
                >
                  {monthNames.map((m, i) => (
                    <option key={i} value={i}>{m}</option>
                  ))}
                </select>
                <select
                  value={currentYear}
                  onChange={(e) => setCurrentYear(parseInt(e.target.value))}
                  className="text-lg font-medium text-[#1C2A46] bg-transparent border-none outline-none cursor-pointer"
                >
                  {[2025, 2026, 2027, 2028, 2029, 2030].map((y) => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <div
                    key={d}
                    className="text-center text-sm font-normal text-[#979797] py-2"
                  >
                    {d}
                  </div>
                ))}
              </div>

              {renderCalendar()}
            </div>

            <div className="flex items-center gap-3">
              <CautionIcon color="#6B635D" size={20} />
              <p className="text-xs text-[#6B635D]">
                Your memories are safe and private. Only the recipient will see
                them when the gift unlocks.
              </p>
            </div>

            {saveError && (
              <p className="text-sm text-red-600 mt-4">{saveError}</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  /* ─────────── Render sub-step content (step 2) ─────────── */
  const renderSubStepContent = () => {
    switch (subStep) {
      case 1:
        return (
          <div className="space-y-8 text-left">
            <div>
              <h2 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Tell the story behind this memory
              </h2>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                This is where the feeling lives. Add context, meaning, and
                personal touches.
              </p>

              <div className="mb-6">
                <label className="block text-base font-normal text-[#1C2A46] mb-2">
                  Page Name
                </label>
                <div className="flex items-center border border-[#E4E4E4] bg-[#F5F5F5] px-4 py-4">
                  <span className="text-[#6B635D] text-sm font-light mr-1 select-none">
                    www.kapta.com/memory/
                  </span>
                  <input
                    type="text"
                    value={pageSlug}
                    onChange={(e) =>
                      setPageSlug(
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9\s-]/g, '')
                          .replace(/\s+/g, '-')
                      )
                    }
                    placeholder="my-18th-birthday"
                    className="flex-1 bg-transparent outline-none text-[#1C2A46] text-sm font-light placeholder-[#979797]"
                  />
                </div>
                <h3 className="text-xs flex items-center gap-1 mt-2 font-normal text-secondary">
                  <CautionIcon className="w-4 h-4" />
                  Accents and special characters are not allowed. Use only
                  letters, numbers, and spaces.
                </h3>
              </div>

              <div className="mb-6">
                <label className="block text-base font-normal text-[#1C2A46] mb-2">
                  Memory Title
                </label>
                <input
                  type="text"
                  value={memoryTitle}
                  onChange={(e) => setMemoryTitle(e.target.value)}
                  placeholder="e.g. `For When You Need a Reminder`"
                  className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#1C2A46] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light placeholder:text-sm"
                />
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8 text-left">
            <div>
              <h2 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Add What You Want Them to Experience
              </h2>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                Upload or record the memory you want to share. You can add one
                type or combine multiple.
              </p>

              <div className="border border-[#E4E4E4] bg-white">
                <div className="border-b border-[#E4E4E4] p-3 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={() => formatText('bold')}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <span className="text-lg font-bold">B</span>
                    </button>
                    <button
                      onClick={() => formatText('italic')}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <span className="text-lg italic">I</span>
                    </button>
                    <button
                      onClick={() => insertEmoji('😊')}
                      className="p-1 hover:bg-gray-100 rounded"
                    >
                      <span className="text-lg">😊</span>
                    </button>
                  </div>
                  <button
                    onClick={toggleExpanded}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                      />
                    </svg>
                  </button>
                </div>

                <textarea
                  ref={textareaRef}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={`Write from the heart...\ne.g. 'Chioma, I wanted you to have something special to remind you how much you mean to me...'`}
                  className={`w-full p-4 resize-none border-none outline-none focus:ring-0 placeholder-gray-400 ${
                    isExpanded ? 'h-96' : 'h-64'
                  } transition-all duration-300`}
                  style={{ fontFamily: 'inherit' }}
                />
              </div>

              <div className="text-sm text-gray-500">
                {description.length} characters
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-8 text-left">
            <div>
              <h2 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Photos and Attachments
              </h2>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                Upload as many photos and videos as you like. Supported
                formats: images and video.
              </p>

              <label
                className={`block border-2 flex-col justify-center items-center border-dashed border-[#E4E4E4] bg-[#F5F5F5] p-12 text-center mb-2 transition-colors cursor-pointer ${
                  isUploadingMedia
                    ? 'cursor-wait opacity-60'
                    : 'hover:border-[#D2A146]'
                }`}
              >
                <input
                  type="file"
                  multiple
                  accept="image/*,video/*"
                  onChange={handleMediaUpload}
                  className="hidden"
                  disabled={isUploadingMedia}
                />
                <div className="flex justify-center items-center">
                  {isUploadingMedia ? (
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D2A146] mb-4" />
                  ) : (
                    <UploadCloudIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                  )}
                </div>
                <p className="text-lg font-medium text-gray-700 mb-2">
                  {isUploadingMedia
                    ? 'Uploading…'
                    : 'Click or drag to add photos / video'}
                </p>
                <p className="text-sm text-gray-500">
                  JPG, PNG, MP4, MOV — no limit
                </p>
              </label>

              {mediaError && (
                <p className="text-xs text-red-600 mt-1">{mediaError}</p>
              )}

              {uploadedImages.length > 0 && (
                <div className="mt-4">
                  <div className="flex gap-3 overflow-x-auto pb-3 snap-x snap-mandatory">
                    {uploadedImages.map((media, index) => (
                      <div
                        key={index}
                        className="relative flex-shrink-0 w-[160px] h-[120px] snap-start group"
                      >
                        {media.type === 'video' ? (
                          <video
                            src={media.url}
                            className="w-full h-full object-cover"
                            muted
                          />
                        ) : (
                          <img
                            src={media.url}
                            alt={media.name}
                            className="w-full h-full object-cover"
                          />
                        )}
                        <button
                          onClick={() => removeMedia(index)}
                          className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          type="button"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-8 text-left">
            <div>
              <h2 className="text-4xl font-medium text-[#1C2A46] mb-2">
                Record or Upload Audio
              </h2>
              <p className="text-[#6B635D] text-base font-normal mb-6">
                Upload a file, paste a link, or record directly in the browser.
              </p>

              <label
                className={`block border-2 flex-col justify-center items-center border-dashed border-[#E4E4E4] bg-[#F5F5F5] p-12 text-center mb-4 transition-colors cursor-pointer ${
                  isUploadingAudio
                    ? 'cursor-wait opacity-60'
                    : 'hover:border-[#D2A146]'
                }`}
              >
                <input
                  type="file"
                  accept="audio/*"
                  onChange={handleAudioUpload}
                  className="hidden"
                  disabled={isUploadingAudio}
                />
                <div className="mx-auto flex items-center justify-center">
                  {isUploadingAudio ? (
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D2A146]" />
                  ) : (
                    <>
                      <MusicFileIcon className="w-16 h-16 text-gray-400" />
                      <RecordOutlineIcon className="w-16 h-16 font-light text-gray-400" />
                    </>
                  )}
                </div>
                <p className="text-lg font-medium text-gray-700 mb-2 mt-4">
                  {isUploadingAudio
                    ? 'Uploading…'
                    : 'Record or upload audio message'}
                </p>
                <p className="text-sm text-gray-500">MP3 (max. 14mb)</p>
              </label>

              {audioError && (
                <p className="text-xs text-red-600 mb-3">{audioError}</p>
              )}

              {audioFile && (
                <div className="mb-4 border border-[#E4E4E4] bg-[#F5F5F5] p-4 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#1C2A46] truncate">
                      {audioFile.name}
                    </p>
                    <audio
                      src={audioFile.url}
                      controls
                      className="w-full mt-2"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleRemoveAudio}
                    className="text-xs text-red-600 hover:underline flex-shrink-0"
                  >
                    Remove
                  </button>
                </div>
              )}

              {!audioFile && !showAudioRecorder && (
                <button
                  type="button"
                  onClick={() => setShowAudioRecorder(true)}
                  className="text-sm text-[#55000C] underline hover:text-[#6a0a17] transition-colors mb-4"
                >
                  Record Audio Instead
                </button>
              )}

              {showAudioRecorder && !audioFile && (
                <div className="mb-4">
                  <AudioRecorder
                    onRecorded={(rec) => {
                      setAudioFile({
                        url: rec.url,
                        publicId: rec.publicId,
                        name: rec.name,
                        duration: rec.duration || null
                      });
                      setAudioUrlInput('');
                      setShowAudioRecorder(false);
                    }}
                    onCancel={() => setShowAudioRecorder(false)}
                  />
                </div>
              )}

              <div className="mb-6">
                <label className="block text-base font-normal text-[#1C2A46] mb-2">
                  Or paste an Audio URL
                </label>
                <input
                  type="text"
                  value={audioUrlInput}
                  onChange={(e) => {
                    setAudioUrlInput(e.target.value);
                    setAudioFile(null);
                  }}
                  placeholder="www.spotify.com/track/superpowers-by-adele"
                  className="w-full px-4 py-4 border border-[#E4E4E4] bg-[#F5F5F5] text-[#1C2A46] focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent placeholder-[#6B635D] placeholder:font-light placeholder:text-sm"
                />
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  /* ─────────── Button config ─────────── */
  const getButtonConfig = () => {
    if (step === 1) {
      return {
        leftButton: {
          label: 'Cancel',
          showArrow: false,
          onClick: () => navigate('/account/future-gifts')
        },
        rightButton: {
          label: 'Next',
          showArrow: true,
          arrowDirection: 'right',
          onClick: handleNext
        }
      };
    } else if (step === 2) {
      if (subStep === 3) {
        /* 🟢 Skip until the user uploads at least one image/video,
           then it becomes "Next". */
        const hasMedia = uploadedImages.length > 0;
        return {
          leftButton: {
            label: 'Previous',
            showArrow: true,
            arrowDirection: 'left',
            onClick: handleBack
          },
          rightButton: hasMedia
            ? {
                label: 'Next',
                showArrow: true,
                arrowDirection: 'right',
                onClick: handleNext
              }
            : {
                label: 'Skip',
                showArrow: true,
                arrowDirection: 'right',
                onClick: handleSkip
              }
        };
      }
      if (subStep === 4) {
        return {
          leftButton: {
            label: 'Previous',
            showArrow: true,
            arrowDirection: 'left',
            onClick: handleBack
          },
          rightButton: {
            label: 'Next',
            showArrow: true,
            arrowDirection: 'right',
            onClick: handleNext
          }
        };
      }
      return {
        leftButton: {
          label: 'Previous',
          showArrow: true,
          arrowDirection: 'left',
          onClick: handleBack
        },
        rightButton: {
          label: subStep === 1 ? 'Continue' : 'Next',
          showArrow: true,
          arrowDirection: 'right',
          onClick: handleNext
        }
      };
    } else if (step === 3) {
      return {
        leftButton: {
          label: 'Previous',
          showArrow: true,
          arrowDirection: 'left',
          onClick: handleBack
        },
        rightButton: {
          label: isSaving ? 'Saving…' : 'Save & link to gift',
          showArrow: false,
          onClick: handleNext,
          disabled: isSaving
        }
      };
    }
  };

  const buttonConfig = getButtonConfig();

  return (
    <div className="min-h-screen pt-14">
      {/* Header — step indicator (left) + "See how it will look" pill (right), same row */}
      <div className="sticky top-0 z-10">
        <div className="container mx-auto max-w-[90rem] px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Left: Step indicator */}
            <div className="flex items-center">
              {stepTitles.map((title, index) => (
                <div key={index} className="flex items-center">
                  <div
                    className={`flex items-center gap-3 p-2 ${
                      step > index + 1
                        ? 'text-[#0088FF]'
                        : step === index + 1
                        ? 'text-[#1C2A46] border-2 border-[#DFDFDF]'
                        : 'text-[#6B635D]'
                    }`}
                  >
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        step > index + 1 || step === index + 1
                          ? 'bg-[#55000C] text-[#F7F5EF]'
                          : 'border-2 border-[#DBDBDB] h-7 w-7 rounded-full text-[#6B635D]'
                      }`}
                    >
                      {index + 1}
                    </div>
                    {step === index + 1 && (
                      <span className="font-medium text-base">{title}</span>
                    )}
                  </div>
                  {index < 2 && <div className="h-0.5 w-20 bg-[#E4E4E4]"></div>}
                </div>
              ))}
            </div>

            {/* Right: See how it will look — same row */}
            <div className="border-2 border-[#DBDBDB] px-4 py-2.5 bg-[#FCF7EE]">
              <p className="text-[#1C2A46] text-sm">
                See how it will look👇🏾
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-[90rem] px-6 py-1">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-32">
          {/* Left Column */}
          <div>
            <div className="max-w-2xl">
              {renderStepContent()}

              <div className="mt-12 pt-8 flex justify-between">
                <button
                  onClick={buttonConfig.leftButton.onClick}
                  disabled={buttonConfig.leftButton.disabled}
                  className="px-8 py-4 border border-[#E4E4E4] text-secondary hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {buttonConfig.leftButton.showArrow &&
                    buttonConfig.leftButton.arrowDirection === 'left' && (
                      <ArrowLeft className="w-4 h-4" />
                    )}
                  {buttonConfig.leftButton.label}
                  {buttonConfig.leftButton.showArrow &&
                    buttonConfig.leftButton.arrowDirection === 'right' && (
                      ""
                    )}
                </button>

                <button
                  onClick={buttonConfig.rightButton.onClick}
                  disabled={buttonConfig.rightButton.disabled}
                  className={`px-8 py-6 w-2/3 justify-center text-white font-medium transition-colors flex items-center gap-2 ${
                    buttonConfig.rightButton.disabled
                      ? 'bg-[#C4956A] cursor-wait'
                      : 'bg-[#D2A146] hover:bg-[#C49660]'
                  }`}
                >
                  {buttonConfig.rightButton.showArrow &&
                    buttonConfig.rightButton.arrowDirection === 'left' && (
                      <ArrowLeft className="w-4 h-4" />
                    )}
                  {buttonConfig.rightButton.label}
                  {buttonConfig.rightButton.showArrow &&
                    buttonConfig.rightButton.arrowDirection === 'right' && (
                      ""
                    )}
                </button>
              </div>
            </div>
          </div>

          {/* Right Column - Safari card */}
          <div>
            <div className="sticky top-28">
              {/* Safari chrome */}
              <div className="rounded-3xl overflow-hidden shadow-xl border-[8px] border-[#D8D8D8] bg-[#D9D9D9]">
                {/* Top chrome bar */}
                <div className="bg-gradient-to-b from-[#EDEDED] to-[#E1E1E1] px-4 py-2.5 flex items-center gap-3 border-b border-[#D0D0D0]">
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                    <span className="w-3 h-3 rounded-full bg-[#FF5F57]" />
                    <span className="w-3 h-3 rounded-full bg-[#FEBC2E]" />
                    <span className="w-3 h-3 rounded-full bg-[#28C840]" />
                  </div>

                  {/* URL bar — live slug */}
                  <div className="flex-1 flex justify-center">
                    <div className="flex items-center gap-2 bg-white border border-[#D0D0D0] rounded-md px-3 py-1.5 w-full max-w-md">
                      <svg
                        className="w-3.5 h-3.5 text-[#6B635D] flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                      >
                        <rect x="5" y="11" width="14" height="10" rx="2" />
                        <path d="M8 11V8a4 4 0 018 0v3" />
                      </svg>
                      <span className="text-xs text-[#6B635D] truncate">
                        www.kapta.com/memory/
                        {pageSlug || 'your-page'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 flex-shrink-0 text-[#6B635D]">
                    <button
                      type="button"
                      className="hover:text-[#1C2A46] transition-colors"
                      title="New Tab"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 4v16m8-8H4"
                        />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="hover:text-[#1C2A46] transition-colors"
                      title="View all tabs"
                    >
                      <svg
                        className="w-4 h-4"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        strokeWidth="2"
                      >
                        <rect x="3" y="3" width="7" height="7" rx="1" />
                        <rect x="14" y="3" width="7" height="7" rx="1" />
                        <rect x="3" y="14" width="7" height="7" rx="1" />
                        <rect x="14" y="14" width="7" height="7" rx="1" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Live preview */}
                <BrowserPreview
                  pageSlug={pageSlug}
                  title={memoryTitle}
                  description={description}
                  audioUrl={previewAudioUrl}
                  audioFileName={audioFile?.name}
                  media={uploadedImages}
                  revealDate={revealDate}
                  revealTime={revealTime}
                  hasContent={hasPreviewContent}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemoryCreation;